import type { MetricRecord } from "@/shared/domain/metrics";
import { latestRecord } from "@/shared/domain/metrics/latestRecord";
import type {
  InMemorySortKey,
  TreeRow,
} from "@/shared/foundation/primitives/Table";

/** One metric in the session: its name and every record published under it. */
export interface MetricLeaf {
  name: string;
  records: MetricRecord[];
  /** The most recently published record. */
  latest: MetricRecord | undefined;
}

/** A row of the metrics table: a group of names, or a metric. */
export type MetricTreeRow = TreeRow<MetricLeaf>;

/** `records` folded into one leaf per metric name, in first-seen order. */
export function metricLeaves(records: readonly MetricRecord[]): MetricLeaf[] {
  const byName = new Map<string, MetricRecord[]>();
  for (const record of records) {
    const existing = byName.get(record.name);
    if (existing) {
      existing.push(record);
    } else {
      byName.set(record.name, [record]);
    }
  }
  return [...byName].map(([name, metricRecords]) => ({
    name,
    records: metricRecords,
    latest: latestRecord(metricRecords),
  }));
}

/**
 * One tree level per dotted name part. Empty parts are kept, so `a..b` stays
 * apart from `a.b`, and each row's path is the metric's own name.
 */
export const metricPath = (leaf: MetricLeaf) => leaf.name.split(".");

export const metricId = (leaf: MetricLeaf) => leaf.name;

/** How many records the metrics in `leaves` hold between them. */
export function recordCount(leaves: readonly MetricLeaf[]): number {
  return leaves.reduce((total, leaf) => total + leaf.records.length, 0);
}

/** What each sortable column sorts a group's metrics by. */
export const METRIC_SORT_KEYS: Record<string, InMemorySortKey<MetricLeaf>> = {
  name: (leaf) => leaf.name,
  records: (leaf) => leaf.records.length,
  value: (leaf) => leaf.latest?.value,
  published: (leaf) =>
    leaf.latest ? new Date(leaf.latest.published) : undefined,
};

/**
 * What groups sort by under the columns that show a value on a group: the
 * group's records under Records. Under the others, groups stay A to Z.
 */
export const METRIC_GROUP_SORT_KEYS: Record<
  string,
  InMemorySortKey<readonly MetricLeaf[]>
> = {
  records: recordCount,
};
